<template>
  <div
    v-if="collection"
    class="page-collection"
    itemscope
    itemtype="http://schema.org/ProductCollection"
  >
    <collection-banner
      v-if="collection.bannerImage"
      :collection="collection"
      :ratio="0.15"
      image-alignment="top"
      class="overflow-hidden mb-2"
      :class="[viewportSize.isGreater('md') ? 'rounded-md' : '']"
    />
    <div
      v-else
      :style="{
        fontSize: '0.55rem', // consistent with TmlCompactBanner
      }"
    >
      <meta itemprop="name" :content="collection.title" />
      <h1 class="title my-2">
        {{ collection.title }}
      </h1>
      <h4 class="description mb-2">
        {{ collection.description }}
      </h4>
    </div>
    <tml-collapsible-text
      v-if="collection.seoDescription"
      :text="collection.seoDescription"
      class="mb-1 text-sm pl-1"
    />
    <div class="navigation-tags-container">
      <div
        v-if="collection.navigationTags?.length"
        class="flex gap-2 mb-2 text-sm navigation-tags"
        :class="{
          'mb-6': viewportSize.isGreater('md'),
          'overflow-x-auto': !viewportSize.isGreater('sm'),
          'flex-wrap': viewportSize.isGreater('sm'),
        }"
      >
        <div v-if="!viewportSize.isGreater('sm')" class="fade-overlay z-10" />

        <tml-anchor
          v-for="(tag, index) in collection.navigationTags"
          :key="index"
          :href="ensureTrailingSlash(tag.href)"
          :class="
            !viewportSize.isGreater('sm') &&
              index === collection.navigationTags.length - 1
              ? 'mr-6'
              : ''
          "
        >
          <tml-tag class="!m-0 p-0">
            {{ tag.text }}
          </tml-tag>
        </tml-anchor>
      </div>
    </div>

    <template v-if="products">
      <collection-new-filter
        v-if="viewportSize.isGreater('md')"
        class="mt-4"
        :products="products"
        :filter="filter"
        :filters="filters"
        :show-header="viewportSize.isGreater('lg')"
        :button-font="collection.banner ? 'var(--tml-heading-3-font)' : ''"
        show-color-squares
        @filter-set="getProducts($event)"
        @sort="
          sort => {
            sortProducts(sort);
          }
        "
      />
    </template>

    <div
      v-if="!viewportSize.isGreater('md')"
      id="mobile-filter"
      class="section-mobile-filter pt-4 mb-4"
    >
      <div class="row">
        <tml-sidebar name="collection-filter" align="right">
          <div>
            <collection-filter
              v-if="products"
              :products="products"
              :filter="filter"
              :filters="filters"
              @filter-set="getProducts($event)"
            />
          </div>
        </tml-sidebar>
        <div class="pr-0 col-7">
          <collection-sort
            @sort="
              sort => {
                sortProducts(sort);
              }
            "
          />
        </div>

        <div class="col-5">
          <!-- Button uses form colours and border radius to be match the 'Sort by' selector -->
          <tml-button
            class="h-full whitespace-nowrap"
            text="Filters"
            :icon="faSlidersH"
            border-color="var(--tml-form-border-color)"
            text-color="var(--tml-form-text-secondary-color)"
            border-radius="var(--tml-form-border-radius)"
            @click="
              $store.dispatch('overlayElements/toggle', 'collection-filter')
            "
          />
        </div>
      </div>
    </div>
    <collection-product-list
      v-if="products && !loadingProducts"
      :products="products"
    />
    <tml-spinner v-if="loadingProducts" :ratio="0.5" />
    <tml-message
      v-if="products && !products.length && !loadingProducts"
      text="Sorry, no products were found."
    />
    <collection-review-summary
      v-show="!loadingProducts && hasReviewsEnabled"
      :collection="collection"
    />
  </div>
  <tml-spinner v-else height="50vh" />
</template>

<script>
import {
  ensureTrailingSlash,
  formatUrl,
  viewportSize,
} from '@teemill/common/helpers';
import {snackbar} from '@teemill/common/services';
import {splitTests} from '@teemill/common/classes';
import {escapeJSON, removeNonStandardCharacters} from '@teemill/utilities';
import {NotFoundError, ApiError} from '@teemill/common/errors';
import {faSlidersH, faSearch} from '@fortawesome/pro-light-svg-icons';
import {tracker} from '@teemill/common/plugins';
import {useHead} from '@vueuse/head';
import {computed} from 'vue';
import {captureException} from '@sentry/vue';
import {uniqBy} from 'lodash';

import {
  CollectionBanner,
  CollectionProductList,
  CollectionFilter,
  CollectionNewFilter,
  CollectionSort,
  CollectionReviewSummary,
} from './partials';

export default {
  name: 'CollectionPage',

  components: {
    CollectionBanner,
    CollectionProductList,
    CollectionFilter,
    CollectionNewFilter,
    CollectionSort,
    CollectionReviewSummary,
  },

  data() {
    return {
      faSlidersH,
      faSearch,

      splitTests,

      sortBy: null,
      filter: {
        filteredProducts: [],
        filterOptions: {
          types: {
            multiselect: true,
            options: [],
            filterableOptions: [],
          },
          sizes: {
            multiselect: true,
            options: [],
            filterableOptions: [],
          },
          colours: {
            multiselect: true,
            options: [],
            filterableOptions: [],
          },
          brands: {
            multiselect: true,
            options: [],
            filterableOptions: [],
          },
        },
        stagedFiltered: [],
        filterMeta: {
          enableFilters: false,
          enabledFilters: 0,
        },
      },
      collection: null,
      products: null,
      formatUrl,
      filters: null,
      colours: null,
      sizes: null,
      types: null,
      brands: null,
      sort: null,
      loadingProducts: true,

      meta: {},
      viewportSize,
      longDescriptionLength: 150,
    };
  },

  computed: {
    filtersEmpty() {
      return (
        this.filter.filterMeta.enableFilters &&
        !this.filter.filteredProducts.length > 0
      );
    },

    url() {
      const division = this.$store.state.subdomain.division;

      return `/omnis/v3/division/${division}/products/`;
    },

    isRapanui() {
      return this.$store.state.subdomain?.division === 14;
    },

    descriptionLength() {
      return this.collection?.description?.length ?? 0;
    },

    hasReviewsEnabled() {
      if (this.$store.state.subdomain.reviews === null) {
        return true;
      }

      return !!this.$store.state.subdomain.reviews?.enabled;
    },
  },

  watch: {
    ['$route.params.collection']: {
      immediate: true,
      handler(value) {
        if (!value || this.collection?.urlName === value) {
          return;
        }

        this.collection = null;
        this.products = null;
        this.sort = null;

        this.fetchData(value);
      },
    },

    filters: {
      handler(filters) {
        setTimeout(() => {
          this.$router.replace({
            query: {
              ...this.$route.query,
              ...filters,
            },
          });
        });
      },
    },
  },

  created() {
    useHead(
      computed(() => {
        if (
          this.collection &&
          this.products &&
          this.$route.name === 'collection'
        ) {
          const host = `${location.protocol}//${location.hostname}`;

          const uniqueProducts = uniqBy(
            this.products.filter(product => product),
            'id'
          ).slice(0, 20);
          const items = uniqueProducts.map((product, index) => {
            const productUrl = `${host}/product/${product.urlName}/`;

            return `
              {
                "@type": "ListItem",
                "position": "${index + 1}",
                "item": {
                  "@type": "Product",
                  "name": "${escapeJSON(product.name)}",
                  "sku": "${product.id}",
                  "url": "${productUrl}",
                  "image": "${product.collectionImage}",
                  "description": "${this.getEscapedDescription(product)}",
                  "brand": {
                    "@type": "Brand",
                    "name": "${this.$store.state.subdomain.company.name}"
                  },
                  ${
                    product.reviews?.count
                      ? `
                        "aggregateRating": {
                          "@type": "AggregateRating",
                          "ratingValue": "${product.reviews.rating}",
                          "reviewCount": "${product.reviews.count}"
                        },`
                      : ''
                  }
                  "offers": [
                    ${this.$store.getters['currency/getLdJson']({
                      price: product.price,
                      url: productUrl,
                    }).join(',')}
                  ]
                }
              }
            `;
          });

          return {
            title: this.collection.meta.title
              ? this.collection.meta.title
              : this.$store.state.title,

            script: [
              {
                type: 'application/ld+json',
                children: `
                {
                  "@context": "http://schema.org/",
                  "@type": "WebPage",
                  "name": "${escapeJSON(this.collection.title)}",
                  "url": "${host}${this.$route.fullPath}",
                  "image": "${this.collection.meta.image}",
                  "description": "${escapeJSON(this.collection.description)}"
                }
              `,
              },
              {
                type: 'application/ld+json',
                children: `
                {
                  "@context": "http://schema.org/",
                  "@type": "ItemList",
                  "name": "${escapeJSON(this.collection.title)}",
                  "url": "${host}${this.$route.fullPath}",
                  "numberOfItems": ${this.products.length},
                  "itemListElement": [
                    ${items.join(',')}
                  ]
                }
              `,
              },
            ],

            meta: [
              {
                vmid: 'og:title',
                property: 'og:title',
                content: this.collection.meta.title
                  ? this.collection.meta.title
                  : this.$store.state.title,
              },
              {
                vmid: 'og:description',
                property: 'og:description',
                content: this.collection.meta.description
                  ? this.collection.meta.description
                  : this.collection.description,
              },
              {
                vmid: 'og:image',
                property: 'og:image',
                content: this.collection.meta.image,
              },
              {vmid: 'og:type', property: 'og:type', content: 'website'},

              {
                vmid: 'twitter:title',
                property: 'twitter:title',
                content: this.collection.meta.title
                  ? this.collection.meta.title
                  : this.$store.state.title,
              },
              {
                vmid: 'twitter:card',
                property: 'twitter:card',
                content: 'photo',
              },
              {
                vmid: 'twitter:image',
                property: 'twitter:image',
                content: this.collection.meta.image,
              },
              {
                vmid: 'description',
                name: 'description',
                content: this.collection.meta.description
                  ? this.collection.meta.description
                  : this.collection.description,
              },
            ],
            link: [
              {
                vmid: 'canonical',
                rel: 'canonical',
                href: `${host}/collection/${this.collection.urlName}/`,
              },
            ],
          };
        }

        return {};
      })
    );
  },

  deactivated() {
    this.$store.commit('collection/clearActive');
  },

  methods: {
    fetchData(collection) {
      const collectionRequest = this.getCollection(collection);

      const productsRequest = this.getProductsFromQueryParameters();

      Promise.all([collectionRequest, productsRequest]).then(() => {
        if (this.collection && this.products) {
          this.$store.commit(
            'collection/setLastVisited',
            Object.assign({}, this.collection, {
              products: this.products.map(product => product.id),
            })
          );

          this.$store.commit('collection/setActive', this.collection);

          this.$store.dispatch('setTitle', this.collection.title);
        }

        this.$eventBus.emit('tml-breadcrumbs-update');
      });

      this.getFilterOptions();
    },

    getCollection(collection) {
      return fetch(
        formatUrl(
          `/omnis/v3/division/${this.$store.state.subdomain.division}/collections/${collection}/`
        ),
        {
          credentials: 'include',
          mode: 'no-cors',
        }
      )
        .then(response => {
          if (response.ok) {
            return response.json();
          }

          if (response.status === 404) {
            throw new NotFoundError();
          }

          throw new ApiError();
        })
        .then(data => {
          this.collection = data;

          tracker.event('view-collection', {
            items: [this.collection.urlName],
          });
        })
        .catch(error => {
          if (error instanceof NotFoundError) {
            this.$router.replace('/404/');
            return;
          }

          if (error instanceof ApiError) {
            snackbar.error('Unable to load collection data, try again later.');
            return;
          }

          captureException(error);
        });
    },
    sortProducts(sortType = null) {
      if (sortType) {
        this.sort = sortType;
      } else {
        this.sort = null;
      }

      this.loadProducts();
    },

    getProducts(filters = null) {
      this.filters = Object.assign({}, filters);

      if (filters) {
        if (filters.colours.length) {
          this.colours = filters.colours;
        } else {
          this.colours = null;
        }

        if (filters.sizes.length) {
          this.sizes = filters.sizes;
        } else {
          this.sizes = null;
        }

        if (filters.types.length) {
          this.types = filters.types;
        } else {
          this.types = null;
        }

        if (filters.brands?.length) {
          this.brands = filters.brands;
        } else {
          this.brands = null;
        }
      }

      return this.loadProducts();
    },

    loadProducts() {
      this.loadingProducts = true;

      return this.axios
        .get(formatUrl(this.url), {
          params: {
            collection: this.$route.params.collection,
            colours: this.colours,
            sizes: this.sizes,
            types: this.types,
            brands: this.brands,
            sort: this.sort === 'recommended' ? null : this.sort,
          },
        })
        .success(data => {
          this.products = data;
          this.loadingProducts = false;
        })
        .handle(404, () => {
          this.$router.replace('/404/');
        })
        .oops('Unable to load product data, try again later.');
    },

    getEscapedDescription(product) {
      return removeNonStandardCharacters(
        escapeJSON(
          product.shortDescription
            ? product.shortDescription
            : product.description
        )
      );
    },

    getProductsFromQueryParameters() {
      const queryParameters = this.$route.query;

      const filterValue = {
        colours: [],
        types: [],
        sizes: [],
        brands: [],
      };

      Object.keys(filterValue).forEach(filterType => {
        const queryValue = queryParameters[filterType];

        if (queryValue) {
          filterValue[filterType] =
            typeof queryValue === 'string' ? queryValue.split(',') : queryValue;
        }
      });

      return this.getProducts(filterValue);
    },

    getFilterOptions() {
      return this.axios
        .get(
          formatUrl(
            `/omnis/v3/division/${this.$store.state.subdomain.division}/collections/${this.$route.params.collection}/options/`
          )
        )
        .success(data => {
          this.loadedFilterOptions = true;
          this.filter.filterOptions.colours.filterableOptions = data.colors.map(
            color => ({
              property: color,
              selected: this.filters.colours.includes(color),
            })
          );
          this.filter.filterOptions.sizes.filterableOptions = data.sizes.map(
            size => ({
              property: size,
              selected: this.filters.sizes.includes(size),
            })
          );
          this.filter.filterOptions.types.filterableOptions = data.types.map(
            type => ({
              property: type,
              selected: this.filters.types.includes(type),
            })
          );
          this.filter.filterOptions.brands.filterableOptions =
            data.brands?.map(brand => ({
              property: brand,
              selected: this.filters.brands.includes(brand),
            })) ?? [];
        })
        .oops(
          "Oops.. we couldn't load your filter options, please try again later."
        );
    },

    ensureTrailingSlash,
  },
};
</script>

<style scoped lang="scss">
.page-collection {
  .collection-filter-container {
    min-height: 280px;
  }

  .section-desktop-filter {
    border-radius: $border-radius;
    min-height: calc(4rem + 2px);
  }

  .section-mobile-filter {
    background-color: var(--tml-page-background-color);
    min-height: calc(4rem + 2px);
  }
}

.navigation-tags-container {
  position: relative;
  max-width: calc(100vw - 3em);

  .navigation-tags {
    .fade-overlay {
      position: absolute;
      pointer-events: none;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      box-shadow: inset -32px 0px 10px -10px var(--tml-page-background-color);
    }

    ::-webkit-scrollbar {
      display: none;
    }
  }
}

.heading {
  font-size: 3.5rem;
}

@include media-breakpoint-down(sm) {
  .heading {
    font-size: 2.5rem;
  }
}

// match collection font sizes to TmlBanner sizes
.title {
  line-height: 125% !important;
  font-size: Max(2.5em, 28px);
}

.description {
  line-height: 0.1em;
  font-size: Max(1em, 16px);
}

@media (min-width: theme('screens.sm')) {
  .title {
    font-size: Max(4em, 38px);
  }
  .description {
    font-size: Max(1.1em, 18px);
  }
}

@media (min-width: theme('screens.md')) {
  .title {
    font-size: Max(4.5em, 38px);
    line-height: 120% !important;
  }
  .description {
    font-size: Max(1.25em, 18px);
  }
}

@media (min-width: theme('screens.lg')) {
  .title {
    font-size: Max(5em, 38px);
    line-height: 110% !important;
  }
  .description {
    font-size: Max(1.25em, 18px);
  }
}

@media (min-width: theme('screens.xl')) {
  .title {
    font-size: Max(6em, 38px);
    line-height: 100% !important;
  }
  .description {
    font-size: Max(1.5em, 18px);
  }
}
</style>
